import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Slide from "@mui/material/Slide";
import Container from "@mui/material/Container";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import DialogHeader from "core/components/DialogHeader";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LockIcon from "@mui/icons-material/Lock";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Функция для построения дерева оглавления на основе level
const buildTocTree = (items) => {
  if (!items || !Array.isArray(items)) return [];

  const tree = [];
  const stack = [];

  items.forEach((item, originalIndex) => {
    const tocItem = { ...item, originalIndex, children: [] };

    // Находим правильную позицию в дереве
    while (stack.length > 0 && stack[stack.length - 1].level >= tocItem.level) {
      stack.pop();
    }

    if (stack.length === 0) {
      // Это элемент первого уровня
      tree.push(tocItem);
    } else {
      // Это дочерний элемент
      stack[stack.length - 1].children.push(tocItem);
    }

    stack.push(tocItem);
  });

  return tree;
};

// Компонент для рендеринга отдельного элемента оглавления
const TocItem = ({
  item,
  hasAccess,
  handleChapterClick,
  currentChapter,
  theme,
  t,
  defaultExpanded = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const isCurrentChapter =
    currentChapter &&
    (currentChapter.id === item.id ||
      currentChapter.title === item.title ||
      currentChapter.order === item.order);

  const handleClick = () => {
    if (hasAccess) {
      handleChapterClick(item, item.originalIndex);
    }
    if (item.children && item.children.length > 0) {
      setExpanded(!expanded);
    }
  };

  return (
    <>
      <Box
        sx={{
          borderLeft:
            item.level > 1 ? `1px solid ${theme.palette.grey[100]}` : "none",
          ml: (item.level - 1) * 1,
          // pl: (item.level - 1) * 0.5,
        }}
      >
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleClick}
            disabled={!hasAccess}
            sx={{
              backgroundColor: isCurrentChapter
                ? "action.hover"
                : "transparent",

              opacity: !hasAccess ? 0.6 : 1,
              borderRadius: 0,
              "&:hover": {
                backgroundColor: hasAccess ? "action.hover" : "transparent",
              },
              "&.Mui-disabled": {
                opacity: 0.6,
              },
            }}
          >
            <ListItemAvatar sx={{ minWidth: 48 }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: !hasAccess
                    ? "grey.400"
                    : item.level === 1
                    ? "primary.main"
                    : "grey.500",
                  fontSize: "0.875rem",
                  border: isCurrentChapter
                    ? `2px solid ${theme.palette.primary.main}`
                    : "none",
                  boxShadow: isCurrentChapter
                    ? `0 0 0 2px ${theme.palette.primary.main}20`
                    : "none",
                }}
              >
                <Typography variant="body2" color="textPrimary">
                  {item.originalIndex + 1}
                </Typography>
              </Avatar>
            </ListItemAvatar>

            <ListItemText
              primary={item.title}
              secondary={item.subtitle}
              primaryTypographyProps={{
                variant: item.level === 1 ? "subtitle1" : "body1",
                fontWeight: isCurrentChapter
                  ? 600
                  : item.level === 1
                  ? 600
                  : 400,
              }}
              secondaryTypographyProps={{
                variant: "caption",
                color: "textSecondary",
                fontWeight: isCurrentChapter ? 500 : 400,
              }}
            />

            <ListItemSecondaryAction>
              <IconButton
                edge="end"
                size="small"
                disabled={!hasAccess}
                onClick={(e) => {
                  e.stopPropagation();
                  if (hasAccess) {
                    handleChapterClick(item, item.originalIndex);
                  }
                }}
                aria-label={
                  hasAccess
                    ? t("book.readChapter", "Читать главу")
                    : t("book.lockedChapter", "Глава заблокирована")
                }
              >
                {hasAccess ? <VisibilityIcon /> : <LockIcon />}
              </IconButton>
            </ListItemSecondaryAction>
          </ListItemButton>
        </ListItem>
        <List component="div" disablePadding>
          {item.children.map((child) => (
            <TocItem
              key={child.originalIndex}
              item={child}
              hasAccess={hasAccess}
              handleChapterClick={handleChapterClick}
              currentChapter={currentChapter}
              theme={theme}
              t={t}
              defaultExpanded={false}
            />
          ))}
        </List>
      </Box>
    </>
  );
};

TocItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    level: PropTypes.number.isRequired,
    order: PropTypes.number,
    originalIndex: PropTypes.number.isRequired,
    children: PropTypes.array,
  }).isRequired,
  hasAccess: PropTypes.bool.isRequired,
  handleChapterClick: PropTypes.func.isRequired,
  currentChapter: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    order: PropTypes.number,
  }),
  theme: PropTypes.shape({
    palette: PropTypes.shape({
      primary: PropTypes.shape({
        main: PropTypes.string,
      }),
      grey: PropTypes.object,
    }),
  }).isRequired,
  t: PropTypes.func.isRequired,
  defaultExpanded: PropTypes.bool,
};

TocItem.defaultProps = {
  currentChapter: null,
  defaultExpanded: false,
};

export default function BookTableOfContentsDialog({
  open,
  onClose,
  tableOfContents,
  bookTitle, // eslint-disable-line no-unused-vars
  contentId,
  hasAccess,
  onChapterClick,
  onReadRequest,
  currentChapter,
}) {
  const { t } = useTranslation("arena");
  const navigate = useNavigate();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  // Создаем дерево оглавления
  const tocTree = useMemo(
    () => buildTocTree(tableOfContents),
    [tableOfContents]
  );

  const handleChapterClick = (item, index) => {
    if (hasAccess) {
      if (onChapterClick) {
        // Используем прямую навигацию через readerControls
        onChapterClick(index);
        onClose(); // Закрываем диалог при прямой навигации
      } else if (onReadRequest) {
        onReadRequest(item);
        onClose();
      } else {
        // Приоритет foliateHref для точной навигации
        if (item.foliateHref) {
          navigate(
            `/book/${contentId}/read?href=${encodeURIComponent(
              item.foliateHref
            )}`
          );
        } else if (item.characterPosition !== undefined) {
          // Fallback на characterPosition
          navigate(`/book/${contentId}/read?pos=${item.characterPosition}`);
        }
        onClose();
      }
    }
  };

  return (
    <Dialog
      fullWidth
      TransitionComponent={Transition}
      fullScreen={fullScreen}
      maxWidth="sm"
      open={open}
      onClose={onClose}
      scroll="paper"
    >
      <DialogHeader
        title={t("book.tableOfContents", "Оглавление")}
        onClose={onClose}
      />

      <DialogContent sx={{ pl: 0, pr: 0, pb: 2 }}>
        <Container disableGutters>
          <List sx={{ py: 0 }}>
            {tocTree.map((item) => (
              <TocItem
                key={item.originalIndex}
                item={item}
                hasAccess={hasAccess}
                handleChapterClick={handleChapterClick}
                currentChapter={currentChapter}
                theme={theme}
                t={t}
                defaultExpanded={true}
              />
            ))}
          </List>
        </Container>
      </DialogContent>
    </Dialog>
  );
}

BookTableOfContentsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  tableOfContents: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      subtitle: PropTypes.string,
      level: PropTypes.number.isRequired,
    })
  ),
  bookTitle: PropTypes.string,
  contentId: PropTypes.string.isRequired,
  hasAccess: PropTypes.bool.isRequired,
  onChapterClick: PropTypes.func,
  onReadRequest: PropTypes.func,
  currentChapter: PropTypes.object,
};

BookTableOfContentsDialog.defaultProps = {
  tableOfContents: [],
  bookTitle: "",
  onChapterClick: null,
  onReadRequest: null,
  currentChapter: null,
};
